import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Trash2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DeleteAccountSetting
 *
 * Permanently deletes the user's account and cascades — messages they sent,
 * conversations where they were the sole remaining participant, their
 * invite codes, and their id from every other user's contacts/blocked
 * lists. GDPR / CCPA right-to-erasure path.
 *
 * Double-gated: user must (1) type their password into an AlertDialog
 * confirming destructive intent and (2) type "DELETE" verbatim into a
 * second input. Matches the panic button's "hard to hit by accident" bar.
 */
export const DeleteAccountSetting = () => {
  const { logout } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = password.length > 0 && confirm === 'DELETE' && !submitting;

  const run = async (closeDialog) => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/delete-account`, { password });
      toast.success('Account deleted. Signing you out…');
      closeDialog?.();
      // Hard reload to clear every piece of local state.
      setTimeout(async () => {
        try {
          await logout();
        } catch {}
        window.location.href = '/login';
      }, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog
      onOpenChange={(open) => {
        if (!open) {
          setPassword('');
          setConfirm('');
        }
      }}
    >
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
          data-testid="delete-account-open"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete account permanently
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-card border-border">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Delete your account?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">
            This cannot be undone. Your account, all messages you sent, your
            invite codes, and your membership in every conversation will be
            permanently removed.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-slate-400">Confirm with your password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="delete-account-password"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-slate-400">
              Type <span className="font-mono text-red-400">DELETE</span> to confirm
            </label>
            <Input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="delete-account-confirm-input"
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              disabled={!canSubmit}
              onClick={(e) => {
                // Radix auto-closes the dialog on Action click; we pass the
                // close via our own post-delete handler so the user sees the
                // toast first.
                e.preventDefault();
                run(() => {
                  const btn = document.querySelector('[data-testid="delete-account-cancel"]');
                  btn?.click();
                });
              }}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
              data-testid="delete-account-confirm"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Permanently delete
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteAccountSetting;
